import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/http';
import { AdminPageHeader } from '@/components/admin/admin-shell';

export const Route = createFileRoute('/admin/clientes/$id')({ component: AdminClienteDetail });

const MODALITY_LABEL: Record<string, string> = {
  ONLINE: 'Online',
  PRESENCIAL: 'Presencial',
  HYBRID: 'Online ou Presencial',
};
const GENDER_LABEL: Record<string, string> = {
  MALE: 'Masculino', male: 'Masculino',
  FEMALE: 'Feminino', female: 'Feminino',
  NON_BINARY: 'Não-binário', 'non-binary': 'Não-binário',
  OTHER: 'Outro', other: 'Outro',
};
const THERAPY_MODALITY_LABEL: Record<string, string> = {
  PSYCHOANALYST: 'Psicanalista',
  PSYCHOTHERAPIST: 'Psicoterapeuta (não-psicólogo)',
  INTEGRATIVE: 'Terapeuta Integrativo',
  HOLISTIC: 'Terapeuta Holístico',
  AROMATHERAPIST: 'Aromaterapeuta',
  FLORAL: 'Terapeuta Floral',
  FAMILY_CONSTELLATION: 'Constelação Familiar',
  HYPNOTHERAPIST: 'Hipnoterapeuta',
};

const money = (v: any) =>
  Number(v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

function calcAge(birthIso?: string | null): number | null {
  if (!birthIso) return null;
  const d = new Date(birthIso);
  if (isNaN(d.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
  return age;
}
function formatPhone(digits: string): string {
  const d = (digits ?? '').replace(/\D/g, '');
  if (d.length === 11) return d.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  if (d.length === 10) return d.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  return digits;
}
function dateStr(iso?: string | Date | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function AdminClienteDetail() {
  const { id } = Route.useParams();
  const { data, isLoading } = useQuery({
    queryKey: ['admin-client', id],
    queryFn: () => api.get(`admin/clients/${id}`).json<any>(),
  });

  if (isLoading || !data) {
    return (
      <>
        <AdminPageHeader title="Cliente" />
        <div className="bg-white rounded-2xl p-12 animate-pulse h-64" />
      </>
    );
  }

  const c = data.client;
  const name = c.user?.name ?? 'Sem nome';
  const initial = name.charAt(0).toUpperCase();
  const avatar =
    (c.avatarUrl && !c.avatarUrl.startsWith('data:') ? c.avatarUrl : null)
    ?? c.user?.image;
  const age = calcAge(c.birthDate);
  const city = [c.city, c.state].filter(Boolean).join(' / ');

  return (
    <>
      <AdminPageHeader title="Cliente" />
      <Link to="/admin/clientes" className="text-sm text-coral-500 hover:underline inline-block mb-4">← Voltar</Link>

      {/* Card do cliente */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-5">
        <div className="flex items-start gap-4 flex-wrap">
          {avatar ? (
            <img src={avatar} alt="" className="size-20 rounded-2xl object-cover ring-4 ring-teal-50 shrink-0" />
          ) : (
            <div className="size-20 rounded-2xl bg-gradient-to-br from-teal-300 to-coral-300 flex items-center justify-center text-white text-3xl font-bold shrink-0">
              {initial}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-extrabold text-slate-900 leading-tight">{name}</h2>
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-sm text-slate-500">
              {c.user?.email && <span>{c.user.email}</span>}
              {c.phone && (
                <>
                  <span>·</span>
                  <span>
                    {formatPhone(c.phone)}
                    {c.whatsappOptIn && <span className="ml-1 text-green-600" title="Recebe WhatsApp">📱</span>}
                  </span>
                </>
              )}
            </div>
            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5 text-sm text-slate-500">
              {age != null && <span>{age} anos</span>}
              {c.gender && <span>· {GENDER_LABEL[c.gender] ?? c.gender}</span>}
              {city && <span>· {city}</span>}
              {c.preferredModality && <span>· prefere {MODALITY_LABEL[c.preferredModality] ?? c.preferredModality}</span>}
            </div>
            <p className="mt-2 text-xs text-slate-400">Cadastrado em {dateStr(c.user?.createdAt)}</p>
          </div>
        </div>

        {/* Métricas */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Metric label="Profissionais" value={String(data.summary.therapistCount)} />
          <Metric label="Sessões totais" value={String(data.summary.totalSessions)} />
          <Metric label="Realizadas" value={String(data.summary.doneSessions)} />
          <Metric label="Canceladas" value={String(data.summary.cancelledSessions)} />
        </div>

        {c.notes && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-1">Observações do cliente</p>
            <p className="text-sm text-slate-600 whitespace-pre-line">{c.notes}</p>
          </div>
        )}
      </div>

      {/* Profissionais atendidos */}
      <h3 className="text-lg font-bold text-slate-800 mb-3">Profissionais que já atenderam este cliente</h3>
      {data.therapists.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 text-center text-sm text-slate-400">
          Nenhuma sessão registrada ainda.
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-slate-50">
                <tr>
                  <Th>Profissional</Th>
                  <Th>Categoria</Th>
                  <Th align="right">Sessões</Th>
                  <Th align="right">Realizadas</Th>
                  <Th>Primeira</Th>
                  <Th>Última</Th>
                  <Th align="right">Total pago</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.therapists.map((t: any) => {
                  const th = t.therapist;
                  const catLabel = th.professionalCategory === 'PSYCHOLOGIST'
                    ? 'Psicólogo(a)'
                    : th.professionalCategory === 'THERAPIST'
                      ? (THERAPY_MODALITY_LABEL[th.therapyModality] ?? 'Terapeuta')
                      : '—';
                  const crpBadge = th.professionalCategory === 'PSYCHOLOGIST' && th.crpNumber
                    ? `CRP ${th.crpRegion}/${th.crpNumber.split('/').pop()}`
                    : null;
                  return (
                    <tr key={th.id} className="hover:bg-slate-50/60">
                      <td className="px-5 py-3">
                        <Link to="/admin/terapeutas/$id" params={{ id: th.id }} className="flex items-center gap-3 group">
                          {th.user?.image ? (
                            <img src={th.user.image} alt="" className="size-9 rounded-full object-cover shrink-0" />
                          ) : (
                            <div className="size-9 rounded-full bg-gradient-to-br from-teal-300 to-teal-500 flex items-center justify-center text-white font-bold shrink-0">
                              {(th.user?.name ?? '?').charAt(0).toUpperCase()}
                            </div>
                          )}
                          <span className="font-semibold text-slate-800 group-hover:text-teal-600 truncate">{th.user?.name ?? '—'}</span>
                        </Link>
                      </td>
                      <td className="px-5 py-3">
                        <span className="text-xs">{catLabel}</span>
                        {crpBadge && (
                          <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 border border-teal-100 whitespace-nowrap">
                            {crpBadge}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-right font-semibold text-slate-700 tabular-nums">{t.totalSessions}</td>
                      <td className="px-5 py-3 text-right font-semibold text-slate-700 tabular-nums">{t.doneSessions}</td>
                      <td className="px-5 py-3 text-xs text-slate-500 whitespace-nowrap">{dateStr(t.firstSessionAt)}</td>
                      <td className="px-5 py-3 text-xs text-slate-500 whitespace-nowrap">{dateStr(t.lastSessionAt)}</td>
                      <td className="px-5 py-3 text-right text-slate-700 tabular-nums">{money(t.totalPaid)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-slate-50 rounded-lg px-3 py-2">
      <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{label}</p>
      <p className="text-lg font-bold text-slate-800 mt-0.5">{value}</p>
    </div>
  );
}

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return <th className={`text-${align} px-5 py-3 text-slate-500 font-semibold uppercase text-xs`}>{children}</th>;
}
